import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { collection, doc, getDocs, setDoc } from 'firebase/firestore'
import { db, getStorageInstance } from '@/lib/firebase'
import { submitResourceSuggestion, type ResourceSubmissionType, type ResourceSubmission } from '@/lib/resourceSubmissions'
import { useUIStore } from '@/stores/ui'
import { TagChipSelector } from '@/views/CommandCenterResourcesView'

const GUIDE_TAG_PRESETS = ['Legal', 'Healthcare', 'Education', 'Employment', 'Social Services', 'Daily Living', 'Mental Health', 'Financial']

export const CATEGORY_OPTIONS: Record<ResourceSubmissionType, string[]> = {
  guide:      ['🌱 Start Here', '📋 Rights & ID', '🏥 Care & School', '💊 Wellness'],
  program:    ['🏛️ Government', '🌿 NGO / CSR'],
  provider:   ['🧩 Therapy Center', '🏥 Hospital', '🏫 Education', '🤝 Support Organization'],
  job:        ['Full-time', 'Part-time', 'Remote', 'Volunteer', 'Internship'],
  correction: [],
}

const SUGGEST_LABELS: Record<ResourceSubmissionType, { heading: string; desc: string; placeholder: string }> = {
  guide:      { heading: 'Suggest a guide', desc: 'Share practical advice for families. Admin review keeps guides trustworthy.', placeholder: 'What should families know? Why is this useful?' },
  program:    { heading: 'Suggest a program', desc: 'Know a government or NGO program that helps families? Share it here.', placeholder: 'Describe the program, who it helps, and how to apply.' },
  provider:   { heading: 'Suggest a provider', desc: 'Know a therapy center, school, or specialist? Help families find them.', placeholder: 'Describe the provider, what they offer, and who they serve.' },
  job:        { heading: 'Suggest a job listing', desc: 'Know an employer hiring PWD-friendly roles? Share it here.', placeholder: 'Describe the role, company, location, and how to apply.' },
  correction: { heading: 'Submit a correction', desc: 'Found outdated or incorrect info? Let us know.', placeholder: 'What is wrong and what should it say?' },
}

interface GuideCard {
  id: string
  icon: string
  title: string
  category: string
  body: string
  note?: string
  tags?: string[]
}

const guideGroups: { id: string; label: string; cards: GuideCard[] }[] = [
  {
    id: 'start',
    label: '🌱 Start Here',
    cards: [
      { id: 'diagnosis', icon: '🧭', title: 'You just got a diagnosis. Now what?', category: 'First Steps', tags: ['First Steps'], body: "Write down the exact diagnosis, doctor's name, and recommended next steps. Connect with one other parent before trying to research everything at once.", note: 'Grief and relief can both be true. This is the start of understanding support needs, not a fixed prediction.' },
      { id: 'community', icon: '🤝', title: 'Finding your first support circle', category: 'Support', tags: ['Support'], body: 'Ask your therapy center, SPED teacher, or PDAO office about parent groups. Local parent groups often know practical routes faster than official pages.' },
      { id: 'routine', icon: '📅', title: 'Build a routine that actually works', category: 'Daily Life', tags: ['Daily Life'], body: 'Visual schedules and transition warnings usually work better than repeated verbal instructions. Keep routines consistent and preview changes early.' },
    ],
  },
  {
    id: 'rights',
    label: '📋 Rights & ID',
    cards: [
      { id: 'pwd-id', icon: '🪪', title: 'Getting a PWD ID', category: 'PWD ID', tags: ['PWD ID'], body: 'Go to your city or municipal Social Welfare and Development Office. Bring a photo, proof of disability, and valid ID. Children can get a PWD ID.' },
      { id: 'benefits', icon: '💰', title: 'PWD benefits and discounts', category: 'Rights', tags: ['Rights'], body: 'PWD benefits include discounts and VAT exemption on eligible medicines, medical services, transportation, restaurants, and priority lanes.' },
      { id: 'assistance', icon: '📋', title: 'Financial assistance map', category: 'Assistance', tags: ['Assistance', 'Financial'], body: 'DSWD AICS, PCSO Medical Assistance, PDAO support, PhilHealth benefits, and NGO/CSR programs can all help with therapy, medicine, transport, or emergency needs.', note: 'Use the Aid tab here to find programs and application routes.' },
    ],
  },
  {
    id: 'care',
    label: '🏥 Care & School',
    cards: [
      { id: 'therapy-types', icon: '🏥', title: 'Therapy types, in plain language', category: 'Therapy', tags: ['Therapy', 'Healthcare'], body: 'OT helps daily living, sensory, feeding, and fine motor skills. SLP helps communication and AAC. PT helps balance and movement. Behavioral therapy supports skills and regulation.' },
      { id: 'school', icon: '🏫', title: 'Navigating SPED and inclusive school', category: 'Education', tags: ['Education'], body: 'Ask for an Individualized Education Plan. Share a one-page profile: triggers, calming supports, communication style, medical needs, and what motivates your child.' },
      { id: 'transition', icon: '🚌', title: 'Prepare for transitions', category: 'School', tags: ['Education', 'Daily Living'], body: 'Visit new places before the first day, meet key staff, practice the route, and explain what stays the same. Some regression during transition is normal.' },
    ],
  },
  {
    id: 'wellness',
    label: '💊 Wellness',
    cards: [
      { id: 'meltdown', icon: '🌊', title: 'Meltdowns are overload, not bad behavior', category: 'Behavior', tags: ['Behavior', 'Mental Health'], body: 'Reduce sensory input first. Use fewer words. Wait until calm before teaching or explaining. Track triggers to find patterns over time.' },
      { id: 'sleep', icon: '😴', title: 'Sleep supports', category: 'Daily Life', tags: ['Daily Living', 'Healthcare'], body: 'Consistent bedtime routine, dim lights, white noise, and deep pressure help many children. Ask your pediatrician before using supplements.' },
      { id: 'carer', icon: '💚', title: 'Carer burnout is real', category: 'Carer Wellbeing', tags: ['Mental Health', 'Social Services'], body: "You are allowed to be tired. Ask for help before crisis point. Even short breaks matter because your regulation supports your child's regulation." },
    ],
  },
]

export const HARDCODED_GUIDES = guideGroups.flatMap((g) =>
  g.cards.map((c) => ({ ...c, groupLabel: g.label }))
)

export const guideFilters = [
  { id: 'all', label: '🔍 All' },
  ...guideGroups.map((group) => ({ id: group.id, label: group.label })),
]

// Pure form — no toggle button. Parent controls visibility and calls onClose.
const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Volunteer', 'Internship']

const EMOJI_PRESETS: Record<ResourceSubmissionType, string[]> = {
  job:        ['💼', '👩‍💻', '🏥', '🏫', '🏢', '🛒', '🔧', '🎨', '📦', '🚗', '👩‍🍳', '📊', '🧑‍🏫', '💡', '🌐', '📝'],
  provider:   ['🏥', '🧠', '🗣️', '🦽', '👁️', '👂', '🏃', '🧘', '🌈', '💙', '🩺', '🎯', '🏋️', '🤝', '💊', '🌟'],
  program:    ['💰', '🏛️', '🤝', '📋', '🎗️', '🏥', '🌍', '💡', '📦', '🎓', '🏠', '🚌', '🍚', '👶', '🌱', '❤️'],
  guide:      ['📖', '💡', '🗺️', '🧭', '📋', '🌟', '🎓', '💙', '🤝', '🌈', '🏅', '📌', '🔍', '✅', '💬', '🧩'],
  correction: ['✏️', '🔍', '⚠️', '📝'],
}

function IconPickerField({ value, onChange, type }: { value: string; onChange: (v: string) => void; type: ResourceSubmissionType }) {
  const [open, setOpen] = useState(false)
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
  const [uploading, setUploading] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const emojis = EMOJI_PRESETS[type]
  const isUrl = value.startsWith('http')

  function handleOpen() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setPopoverStyle({ position: 'fixed', top: r.bottom + 8, left: r.left, zIndex: 9999 })
    }
    setOpen((v) => !v)
  }

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const storage = await getStorageInstance()
      const path = `suggestion-icons/${Date.now()}-${file.name}`
      await uploadBytes(storageRef(storage, path), file)
      const url = await getDownloadURL(storageRef(storage, path))
      onChange(url)
      setOpen(false)
    } catch {
      // silent — user can still pick an emoji
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div>
      <button ref={btnRef} type="button" onClick={handleOpen}
        className="w-12 h-12 rounded-xl border border-slate-300 bg-white flex items-center justify-center text-xl hover:bg-slate-50 overflow-hidden flex-shrink-0">
        {value
          ? isUrl ? <img src={value} className="w-8 h-8 object-cover rounded-lg" /> : value
          : <span className="text-slate-300 text-2xl">🏢</span>}
      </button>
      {open && createPortal(
        <div style={popoverStyle} className="bg-white border border-slate-200 rounded-2xl shadow-xl p-3 w-64 space-y-3">
          <div className="grid grid-cols-8 gap-0.5">
            {emojis.map((e) => (
              <button key={e} type="button" onClick={() => { onChange(e); setOpen(false) }}
                className={`w-7 h-7 rounded-lg flex items-center justify-center text-base hover:bg-slate-100 transition-colors ${value === e ? 'bg-beacon-50 ring-1 ring-beacon-400' : ''}`}>
                {e}
              </button>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-2 space-y-2">
            <input type="text" value={isUrl ? '' : value} onChange={(e) => onChange(e.target.value.slice(0, 2))} maxLength={2}
              placeholder="Custom emoji" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-beacon-400" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-full py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
              <i className="fa-solid fa-arrow-up-from-bracket text-[10px]" />
              {uploading ? 'Uploading…' : 'Upload image'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export function ResourceSuggestForm({ defaultType, onClose, countryOptions, open = true }: { defaultType: ResourceSubmissionType; onClose: () => void; countryOptions?: string[]; open?: boolean }) {
  const showToast = useUIStore((s) => s.showToast)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [icon, setIcon] = useState('')
  const [country, setCountry] = useState('')
  const [city, setCity] = useState('')
  const [amount, setAmount] = useState('')
  const [contact, setContact] = useState('')
  const [company, setCompany] = useState('')
  const [location, setLocation] = useState('')
  const [jobType, setJobType] = useState('')
  const [pay, setPay] = useState('')
  const [payPeriod, setPayPeriod] = useState('')
  const [remote, setRemote] = useState(false)
  const [ra7277, setRa7277] = useState(false)
  const [pwdDiscount, setPwdDiscount] = useState(false)
  const [rate, setRate] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [tags, setTags] = useState('')
  const [note, setNote] = useState('')
  const [link, setLink] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const labels = SUGGEST_LABELS[defaultType]

  async function submit() {
    if (!title.trim() || !description.trim()) return
    setSubmitting(true)
    const extraLines =
      defaultType === 'program'
        ? [icon && `Icon: ${icon}`, amount && `Amount: ${amount}`, contact && `Contact: ${contact}`, country && `Country: ${country}`, city && `City: ${city}`].filter(Boolean).join('\n')
        : defaultType === 'job'
        ? [icon && `Icon: ${icon}`, company && `Company: ${company}`, location && `City: ${location}`, jobType && `Type: ${jobType}`, pay && `Pay: ${pay}${payPeriod ? ` ${payPeriod}` : ''}`, remote && 'Remote: Yes', country && `Country: ${country}`, ra7277 && 'RA 7277 Compliant: Yes'].filter(Boolean).join('\n')
        : defaultType === 'provider'
        ? [icon && `Icon: ${icon}`, address && `Address: ${address}`, phone && `Phone: ${phone}`, rate && `Rate: ${rate}`, city && `City: ${city}`, country && `Country: ${country}`, ra7277 && 'RA 7277 Compliant: Yes', pwdDiscount && 'PWD Discount: Yes'].filter(Boolean).join('\n')
        : defaultType === 'guide'
        ? [icon && `Icon: ${icon}`, tags && `Tags: ${tags}`, note && `Note: ${note}`, country && `Country: ${country}`, city && `City: ${city}`].filter(Boolean).join('\n')
        : ''
    const fullDescription = extraLines ? `${extraLines}\n\n${description}` : description
    try {
      await submitResourceSuggestion({ type: defaultType, title, category, link, description: fullDescription, tags: tags.split(',').map(s => s.trim()).filter(Boolean) })
      setTitle(''); setCategory(''); setIcon(''); setCountry(''); setCity(''); setAmount(''); setContact(''); setCompany(''); setLocation(''); setJobType(''); setPay(''); setPayPeriod(''); setRemote(false); setRa7277(false); setPwdDiscount(false); setRate(''); setAddress(''); setPhone(''); setTags(''); setNote(''); setLink(''); setDescription('')
      onClose()
      showToast('Suggestion sent for review', 'success')
    } catch (err) {
      console.warn('[ResourceSuggestForm] submit failed:', err)
      showToast('Could not send suggestion', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls = 'w-full rounded-xl bg-white border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400 placeholder:text-slate-400'
  const hasCountryOptions = countryOptions && countryOptions.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10 flex flex-col max-h-[92vh]">
        <div className="flex justify-center pt-3 pb-1 sm:hidden"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100 flex-shrink-0">
          <div>
            <h3 className="font-bold text-base text-slate-900">{labels.heading}</h3>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{labels.desc}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200 flex-shrink-0"><i className="fa-solid fa-xmark text-sm" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
      <div className="grid grid-cols-[3rem_1fr] gap-3 items-start">
        <IconPickerField value={icon} onChange={setIcon} type={defaultType} />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={defaultType === 'job' ? 'Job title' : 'Title or resource name'} className={inputCls} />
      </div>
      {CATEGORY_OPTIONS[defaultType].length > 0 ? (
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} text-slate-700`}>
          <option value="">{defaultType === 'guide' ? '— Select a section —' : '— Select a category —'}</option>
          {CATEGORY_OPTIONS[defaultType].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Describe what this corrects" className={inputCls} />
      )}
      {defaultType === 'job' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className={inputCls} />
            <div>
              <input value={location} onChange={(e) => setLocation(e.target.value)} list="job-location-suggestions" placeholder="City" className={inputCls} />
              <datalist id="job-location-suggestions">
                <option value="Metro Manila" />
                <option value="Cebu City" />
                <option value="Davao City" />
                <option value="Quezon City" />
                <option value="Makati" />
              </datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={jobType} onChange={(e) => setJobType(e.target.value)} className={`${inputCls} text-slate-700`}>
              <option value="">— Job type —</option>
              {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex gap-2">
              <input value={pay} onChange={(e) => setPay(e.target.value)} placeholder="Rate (e.g. ₱25,000)" className={`${inputCls} flex-1 min-w-0`} />
              <select value={payPeriod} onChange={(e) => setPayPeriod(e.target.value)} className="rounded-xl bg-white border border-slate-300 px-3 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-beacon-400 flex-shrink-0">
                <option value="">— rate —</option>
                <option value="/mo">/mo</option>
                <option value="/hr">/hr</option>
                <option value="/day">/day</option>
                <option value="/project">/project</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">💻 Remote</p>
                <p className="text-xs text-slate-400">Work from anywhere</p>
              </div>
            </label>
            <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={ra7277} onChange={(e) => setRa7277(e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">⚖️ RA 7277</p>
                <p className="text-xs text-slate-400">Actively hires PWDs</p>
              </div>
            </label>
          </div>
        </>
      )}
      {defaultType === 'provider' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" className={inputCls} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" className={inputCls} />
          </div>
          <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate (e.g. ₱600/session, PhilHealth covered)" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={ra7277} onChange={(e) => setRa7277(e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">⚖️ RA 7277</p>
                <p className="text-xs text-slate-400">PWD-accessible, meets legal requirements</p>
              </div>
            </label>
            <label className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={pwdDiscount} onChange={(e) => setPwdDiscount(e.target.checked)} className="accent-beacon-600 w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-semibold text-slate-800">💙 PWD Discount</p>
            </label>
          </div>
        </>
      )}
      {defaultType === 'program' && (
        <div className="grid grid-cols-2 gap-3">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Aid amount (e.g. Up to ₱5,000)" className={inputCls} />
          <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone or contact info" className={inputCls} />
        </div>
      )}
      {defaultType === 'job' && (
        <TagChipSelector value={tags} onChange={setTags} presets={['Remote', 'Flexible Hours', 'Training Provided', 'Part-time', 'PWD-Friendly', 'Entry Level', 'Immediate Hire']} placeholder="Custom tag (press Enter to add)" />
      )}
      {defaultType === 'provider' && (
        <TagChipSelector value={tags} onChange={setTags} presets={['Wheelchair Ramp', 'Elevator', 'Accessible Restroom', 'Braille Signage', 'Sign Language Staff', 'Quiet Room', 'Service Animal Friendly', 'PWD Parking']} placeholder="Custom accessibility tag (press Enter to add)" />
      )}
      {(defaultType === 'program' || defaultType === 'guide') && (
        <TagChipSelector value={tags} onChange={setTags} presets={defaultType === 'guide' ? GUIDE_TAG_PRESETS : ['Cash Assistance', 'Medical', 'Education', 'Housing', 'Food', 'PWD-Specific', 'Mental Health', 'Legal Aid', 'Transportation', 'Emergency Relief']} placeholder="Custom tag (press Enter to add)" />
      )}
      {(defaultType === 'program' || defaultType === 'provider' || defaultType === 'guide') && (
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (optional)" className={inputCls} />
      )}
      {hasCountryOptions && (
        <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${inputCls} text-slate-700`}>
          <option value="">— Select a country —</option>
          {countryOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      )}
      <input value={link} onChange={(e) => setLink(e.target.value)} placeholder={defaultType === 'job' ? 'Apply link or website (optional)' : defaultType === 'provider' ? 'Email or website (optional)' : defaultType === 'program' ? 'Apply link or program website (optional)' : 'Reference link (optional)'} className={inputCls} />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder={labels.placeholder} className={`${inputCls} resize-none`} />
      {defaultType === 'guide' && (
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional) — e.g. Grief and relief can both be true." className={`${inputCls} resize-none`} />
      )}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-3 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          <button onClick={submit} disabled={!title.trim() || !description.trim() || submitting} className="flex-1 py-3 rounded-lg bg-beacon-600 text-white text-sm font-semibold hover:bg-beacon-700 disabled:opacity-50 transition-colors">
            {submitting ? 'Sending...' : 'Send for Review'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Self-contained toggle + form — renders as a full-width block, safe to place anywhere.
export function ResourceSuggestSection({ defaultType }: { defaultType: ResourceSubmissionType }) {
  const [open, setOpen] = useState(false)
  if (open) return <ResourceSuggestForm defaultType={defaultType} onClose={() => setOpen(false)} />
  return (
    <button
      onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 text-xs font-semibold text-beacon-700 bg-white border border-beacon-200 hover:bg-beacon-50 rounded-full px-3 py-1.5 transition-colors"
    >
      <i className="fa-solid fa-lightbulb text-[10px]" />
      Suggest
    </button>
  )
}

interface GuideOverride { id: string; icon?: string; title?: string; body?: string; note?: string; tags?: string[]; group?: string }

function parseCommunityGuideNote(description: string): { body: string; note: string } {
  const parts = description.split(/\n\n/)
  const metaBlock = parts[0] ?? ''
  const bodyBlock = parts.slice(1).join('\n\n').trim() || description
  const noteMatch = metaBlock.match(/^Note:\s*(.+)$/m)
  return { body: bodyBlock, note: noteMatch?.[1] ?? '' }
}

export function ResourceGuides({ searchQuery = '', activeGroup = 'all', approvedGuides = [], countryOptions, isAdmin = false, onEditApprovedGuide }: { searchQuery?: string; activeGroup?: string; approvedGuides?: ResourceSubmission[]; countryOptions?: string[]; isAdmin?: boolean; onEditApprovedGuide?: (s: ResourceSubmission) => void }) {
  const showToast = useUIStore((s) => s.showToast)
  const [showSuggest, setShowSuggest] = useState(false)
  const [selectedGuide, setSelectedGuide] = useState<GuideCard | null>(null)
  const [selectedCommunityGuide, setSelectedCommunityGuide] = useState<ResourceSubmission | null>(null)
  const [overrides, setOverrides] = useState<Record<string, GuideOverride>>({})
  const [editingGuide, setEditingGuide] = useState<GuideCard | null>(null)
  const [editDraft, setEditDraft] = useState<{ icon: string; title: string; body: string; note: string; tags: string; group: string } | null>(null)
  const [guideSaving, setGuideSaving] = useState(false)

  useEffect(() => {
    getDocs(collection(db, 'guide_overrides'))
      .then(snap => {
        const map: Record<string, GuideOverride> = {}
        snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() } as GuideOverride })
        setOverrides(map)
      })
      .catch(() => {})
  }, [])

  async function saveGuideOverride() {
    if (!editingGuide || !editDraft) return
    setGuideSaving(true)
    try {
      const parsedTags = editDraft.tags.split(',').map(s => s.trim()).filter(Boolean)
      const override: Omit<GuideOverride, 'id'> = {
        icon: editDraft.icon || undefined,
        title: editDraft.title || undefined,
        body: editDraft.body || undefined,
        note: editDraft.note || undefined,
        tags: parsedTags.length > 0 ? parsedTags : undefined,
        group: editDraft.group || undefined,
      }
      await setDoc(doc(db, 'guide_overrides', editingGuide.id), override)
      setOverrides(prev => ({ ...prev, [editingGuide.id]: { id: editingGuide.id, ...override } }))
      setEditingGuide(null); setEditDraft(null)
      showToast('Guide updated', 'success')
    } catch { showToast('Save failed', 'error') } finally { setGuideSaving(false) }
  }

  function applyOverride(card: GuideCard): GuideCard {
    const o = overrides[card.id]
    if (!o) return card
    return { ...card, icon: o.icon ?? card.icon, title: o.title ?? card.title, body: o.body ?? card.body, note: o.note ?? card.note, tags: o.tags ?? card.tags }
  }

  const normalizedQuery = searchQuery.trim().toLowerCase()

  const filteredApprovedGuides = approvedGuides.filter((g) => {
    if (!normalizedQuery) return true
    return [g.title, g.description, g.category ?? ''].some((v) => v.toLowerCase().includes(normalizedQuery))
  })

  // Build a map of groupId → cards, respecting group overrides
  const groupedCards: Record<string, GuideCard[]> = Object.fromEntries(guideGroups.map(g => [g.id, []]))
  guideGroups.forEach(g => {
    g.cards.forEach(card => {
      const overridden = applyOverride(card)
      const effectiveGroupId = overrides[card.id]?.group ?? g.id
      const target = groupedCards[effectiveGroupId] ?? groupedCards[g.id]
      target.push(overridden)
    })
  })
  const baseGroups = (activeGroup === 'all' ? guideGroups : guideGroups.filter((group) => group.id === activeGroup))
    .map(group => ({ ...group, cards: groupedCards[group.id] ?? [] }))
  const filteredGroups = normalizedQuery
    ? baseGroups
        .map((group) => ({
          ...group,
          cards: group.cards.filter((card) =>
            [card.title, card.category, card.body, card.note ?? '']
              .some((value) => value.toLowerCase().includes(normalizedQuery)),
          ),
        }))
        .filter((group) => group.cards.length > 0)
    : baseGroups

  return (
    <div className="space-y-4">
      {/* Admin guide edit modal */}
      {editingGuide && editDraft && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setEditingGuide(null); setEditDraft(null) }} />
          <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10 flex flex-col max-h-[92vh]">
            <div className="flex justify-center pt-3 pb-1 sm:hidden"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100 flex-shrink-0">
              <h3 className="font-bold text-base text-slate-900">Edit Guide</h3>
              <button onClick={() => { setEditingGuide(null); setEditDraft(null) }} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200"><i className="fa-solid fa-xmark text-sm" /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-[3rem_1fr] gap-3 items-start">
                <input value={editDraft.icon} onChange={e => setEditDraft(d => d && ({ ...d, icon: e.target.value.slice(0, 2) }))} maxLength={2} placeholder={editingGuide.icon} className="w-12 h-12 rounded-xl border border-slate-300 bg-white text-center text-xl focus:outline-none focus:ring-2 focus:ring-beacon-400" />
                <input value={editDraft.title} onChange={e => setEditDraft(d => d && ({ ...d, title: e.target.value }))} placeholder={editingGuide.title} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400" />
              </div>
              <select value={editDraft.group} onChange={e => setEditDraft(d => d && ({ ...d, group: e.target.value }))} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400 text-slate-700">
                {guideGroups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
              <textarea value={editDraft.body} onChange={e => setEditDraft(d => d && ({ ...d, body: e.target.value }))} rows={5} placeholder={editingGuide.body} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400 resize-none" />
              <textarea value={editDraft.note} onChange={e => setEditDraft(d => d && ({ ...d, note: e.target.value }))} rows={2} placeholder={editingGuide.note ?? 'Note (optional)'} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-beacon-400 resize-none" />
              <TagChipSelector value={editDraft.tags} onChange={v => setEditDraft(d => d && ({ ...d, tags: v }))} presets={GUIDE_TAG_PRESETS} placeholder="Custom tag (press Enter to add)" />
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
              <button onClick={() => { setEditingGuide(null); setEditDraft(null) }} className="flex-1 py-3 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={saveGuideOverride} disabled={guideSaving} className="flex-1 py-3 rounded-lg bg-beacon-600 text-white text-sm font-semibold hover:bg-beacon-700 disabled:opacity-50">{guideSaving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Guide detail modal */}
      {selectedGuide && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedGuide(null)} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10 overflow-hidden">
            <div className="flex justify-center pt-3 pb-1 sm:hidden"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
            <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-slate-100 gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className="text-2xl leading-none mt-0.5 flex-shrink-0">{selectedGuide.icon}</span>
                <div className="min-w-0">
                  <h3 className="font-bold text-base text-slate-900 leading-snug">{selectedGuide.title}</h3>
                </div>
              </div>
              <button onClick={() => setSelectedGuide(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200 flex-shrink-0"><i className="fa-solid fa-xmark text-sm" /></button>
            </div>
            <div className="px-5 py-4 space-y-3 max-h-[85vh] overflow-y-auto pb-6">
              <p className="text-[15px] text-slate-700 leading-relaxed">{selectedGuide.body}</p>
              {selectedGuide.note && (
                <p className="text-xs text-beacon-700 bg-beacon-50 border border-beacon-100 rounded-xl px-3 py-2 leading-relaxed">{selectedGuide.note}</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Community guide detail modal */}
      {selectedCommunityGuide && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedCommunityGuide(null)} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-10 overflow-hidden">
            <div className="flex justify-center pt-3 pb-1 sm:hidden"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
            <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-slate-100 gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className="text-2xl leading-none mt-0.5 flex-shrink-0">💡</span>
                <div className="min-w-0">
                  <h3 className="font-bold text-base text-slate-900 leading-snug">{selectedCommunityGuide.title}</h3>
                  <span className="inline-flex mt-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-beacon-50 text-beacon-600 border border-beacon-100">Community</span>
                </div>
              </div>
              <button onClick={() => setSelectedCommunityGuide(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200 flex-shrink-0"><i className="fa-solid fa-xmark text-sm" /></button>
            </div>
            <div className="px-5 py-4 space-y-3 max-h-[85vh] overflow-y-auto pb-6">
              {(() => { const { body, note } = parseCommunityGuideNote(selectedCommunityGuide.description); return (<>
                <p className="text-[15px] text-slate-700 leading-relaxed">{body}</p>
                {note && <p className="text-xs text-beacon-700 bg-beacon-50 border border-beacon-100 rounded-xl px-3 py-2 leading-relaxed">{note}</p>}
              </>); })()}
              {selectedCommunityGuide.link && (
                <a href={selectedCommunityGuide.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-beacon-600 hover:text-beacon-700">
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" /> Learn more
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Practical guides for families</p>
        <button
          onClick={() => setShowSuggest(v => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-beacon-700 bg-white border border-beacon-200 hover:bg-beacon-50 rounded-full px-3 py-1.5 transition-colors flex-shrink-0"
        >
          <i className="fa-solid fa-lightbulb text-[10px]" />
          Suggest a Guide
        </button>
      </div>

      <ResourceSuggestForm open={showSuggest} defaultType="guide" onClose={() => setShowSuggest(false)} countryOptions={countryOptions} />

      {filteredGroups.length === 0 && filteredApprovedGuides.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500">No guides found{searchQuery ? ` for "${searchQuery}"` : ''}</p>
        </div>
      )}

      {filteredGroups.map((group) => (
        <div key={group.id} className="space-y-4">
          {group.cards.map((card) => (
            <div key={card.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
              <div className="p-5 space-y-2 min-h-36">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-beacon-50 flex items-center justify-center flex-shrink-0 text-xl leading-none">{card.icon}</div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-[15px] text-slate-900 truncate">{card.title}</h4>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 flex-shrink-0 whitespace-nowrap">{group.label}</span>
                </div>
                <p className="text-sm text-slate-600 leading-snug line-clamp-2">{card.body}</p>
              </div>
              <div className="flex items-center justify-between gap-2 px-5 py-3 bg-slate-50 border-t border-slate-100">
                <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                  {(card.tags ?? []).map(t => <span key={t} className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {isAdmin && <button onClick={() => { setEditingGuide(card); setEditDraft({ icon: card.icon, title: card.title, body: card.body, note: card.note ?? '', tags: (card.tags ?? []).join(', '), group: group.id }) }} className="w-7 h-7 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-400"><i className="fa-solid fa-pen text-[10px]" /></button>}
                  <button onClick={() => setSelectedGuide(card)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-lg">Read</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {filteredApprovedGuides.length > 0 && (
        <section className="space-y-4">
          <div className="space-y-4">
            {filteredApprovedGuides.map((guide) => (
              <div key={guide.id} className="rounded-2xl border border-beacon-100 bg-white overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
                <div className="p-5 space-y-2 min-h-36">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-beacon-50 flex items-center justify-center flex-shrink-0 text-xl leading-none">💡</div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-[15px] text-slate-900 truncate">{guide.title}</h4>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{guide.category || 'Community Guide'}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-snug line-clamp-2">{guide.description}</p>
                </div>
                <div className="flex items-center justify-between gap-2 px-5 py-3 bg-slate-50 border-t border-slate-100">
                  <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 mr-2">
                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-beacon-50 text-beacon-600 border border-beacon-100 whitespace-nowrap">Community</span>
                    {guide.tags?.map(t => <span key={t} className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {isAdmin && onEditApprovedGuide && (
                      <button onClick={() => onEditApprovedGuide(guide)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-400"><i className="fa-solid fa-pen text-[10px]" /></button>
                    )}
                    <button onClick={() => setSelectedCommunityGuide(guide)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-lg">Read</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
